import type { Tagged } from 'type-fest'
import { z } from 'zod'
import type { NodeId } from './generateNodeId'

export const HLCSchema = z.string().refine(
  (val): val is HLCString => {
    const parts = val.split('-')
    if (parts.length < 3) return false
    const nodeId = parts.pop()
    const counter = parts.pop()
    const time = parts.join('-')
    return (
      !!nodeId && !!counter && !Number.isNaN(new Date(time).getTime()) && !Number.isNaN(Number.parseInt(counter, 16))
    )
  },
  {
    message: 'Invalid HLC string format. Expected ISO-COUNTER-NODEID',
  },
) as z.ZodType<HLCString>

export const hlcString = z.templateLiteral([z.string(), '-', z.string(), '-', z.string()])

type Time = Tagged<string, 'Time'>
type Counter = Tagged<number, 'Counter'>

export type HLCString = `${Time}-${Counter}-${NodeId}`

export interface ParsedHLC {
  time: number
  counter: number
  nodeId: string
}

export function parseHLC(hlc: HLCString): ParsedHLC {
  const parts = hlc.split('-')
  const nodeId = parts.pop() || ''
  const counter = parts.pop() || ''
  const time = parts.join('-')

  return {
    time: new Date(time).getTime(),
    counter: Number.parseInt(counter, 16),
    nodeId,
  }
}

export function formatHLC(hlc: ParsedHLC): HLCString
export function formatHLC(time: number, counter: number, nodeId: string): HLCString
export function formatHLC(arg1: number | ParsedHLC, arg2?: number, arg3?: string): HLCString {
  if (typeof arg1 === 'number') {
    if (arg2 === undefined || arg3 === undefined) {
      throw new Error('counter and nodeId are required when time is a number')
    }
    return `${new Date(arg1).toISOString()}-${arg2.toString(16).padStart(4, '0')}-${arg3}` as HLCString
  }
  const { time, counter, nodeId } = arg1
  return `${new Date(time).toISOString()}-${counter.toString(16).padStart(4, '0')}-${nodeId}` as HLCString
}

/**
 * Normalizes an HLC string by applying a drift offset and capping it to the server time.
 * This is used to mitigate clock drift where clients might send messages with future timestamps.
 */
export function normalizeHLC(
  hlc: HLCString,
  driftOffset: number,
  serverNow: number,
): {
  normalizedHlc: HLCString
  drift: number
} {
  const parsed = parseHLC(hlc)
  const adjustedTime = parsed.time + driftOffset
  const drift = adjustedTime - parsed.time
  return {
    normalizedHlc: formatHLC({
      ...parsed,
      time: Math.min(adjustedTime, serverNow),
    }),
    drift,
  }
}

export interface HLC {
  now: () => HLCString
  receive: (remote: HLCString) => void
}

export function createHLC(nodeId: string): HLC {
  let lastTime: number = 0
  let counter: number = 0

  return {
    now(): HLCString {
      const now = Date.now()
      if (now > lastTime) {
        lastTime = now
        counter = 0
      } else {
        counter++
      }

      return formatHLC(lastTime, counter, nodeId)
    },

    receive(remote: HLCString) {
      const { time: remoteTime, counter: remoteCounter } = parseHLC(remote)

      if (Number.isNaN(remoteTime) || Number.isNaN(remoteCounter)) return

      const now = Date.now()

      const maxTime = Math.max(lastTime, remoteTime, now)

      if (maxTime === lastTime && maxTime === remoteTime) {
        counter = Math.max(counter, remoteCounter) + 1
      } else if (maxTime === remoteTime) {
        counter = remoteCounter + 1
      } else if (maxTime === lastTime) {
        counter++
      } else {
        counter = 0
      }
      lastTime = maxTime
    },
  }
}
